/*
  FijiKava August 2026 redesign — front-end behaviour
  ------------------------------------------------------------
  Loaded only by layout/fk-2026.liquid. Self-contained on purpose:
  the redesign layout does not load Dawn's global.js / product-form.js,
  so nothing here may depend on them.

  Custom elements:
    <fk26-header>   mobile nav toggle + cart count
    <fk26-gallery>  thumbnail -> main image
    <fk26-product>  variant + quantity + purchase mode + add to cart
    <fk26-reveal>   scroll reveal for the effects timeline

  The newsletter form posts natively through Shopify's customer form,
  so it needs no JS.
*/
(function () {
  'use strict';

  /* --------------------------------------------------------- money */
  function formatMoney(cents, format) {
    var value = (cents || 0) / 100;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/.exec(format || '') || [];
    var name = placeholder[1] || 'amount';

    function withDecimals(precision, thousands, decimal) {
      var parts = value.toFixed(precision).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var formatted;
    switch (name) {
      case 'amount_no_decimals':
        formatted = withDecimals(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withDecimals(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDecimals(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDecimals(2, "'", '.');
        break;
      default:
        formatted = withDecimals(2, ',', '.');
    }

    return (format || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------------------ cart count */
  function setCartCount(count) {
    document.querySelectorAll('[data-fk26-cart-count]').forEach(function (el) {
      el.textContent = count;
      var pill = el.closest('.cart-pill');
      if (!pill) return;
      pill.style.transform = 'scale(1.12)';
      setTimeout(function () {
        pill.style.transform = 'scale(1)';
      }, 180);
    });
  }

  function refreshCartCount() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        setCartCount(cart.item_count);
        return cart;
      })
      .catch(function () {
        /* leave the count as it was */
      });
  }

  /* ---------------------------------------------------------- header */
  class FK26Header extends HTMLElement {
    connectedCallback() {
      var toggle = this.querySelector('.nav-toggle');
      var root = this.querySelector('.fk26-header');
      if (!toggle || !root) return;

      toggle.addEventListener('click', function () {
        var open = root.getAttribute('data-nav-open') === 'true';
        root.setAttribute('data-nav-open', open ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    }
  }

  /* --------------------------------------------------------- gallery */
  class FK26Gallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-main-image]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
      var self = this;

      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self.select(thumb);
        });
      });

      document.addEventListener('fk26:variant:change', function (event) {
        var mediaId = event.detail && event.detail.featuredMediaId;
        if (!mediaId) return;
        var match = self.thumbs.filter(function (t) {
          return t.dataset.mediaId === String(mediaId);
        })[0];
        if (match) self.select(match);
      });
    }

    select(thumb) {
      if (!this.main) return;
      this.main.src = thumb.dataset.full || thumb.dataset.thumb;
      if (thumb.dataset.alt) this.main.alt = thumb.dataset.alt;
      this.thumbs.forEach(function (t) {
        t.classList.toggle('active', t === thumb);
      });
    }
  }

  /* --------------------------------------------------------- product */
  class FK26Product extends HTMLElement {
    connectedCallback() {
      this.data = readJSON(this.querySelector('[data-product-json]'));
      if (!this.data) return;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold || '0', 10);
      this.form = this.querySelector('form[data-product-form]');
      this.variantInput = this.querySelector('input[name="id"][data-variant-id]');
      this.sellingPlanInput = this.querySelector('[data-selling-plan-id]');
      this.qtyInput = this.querySelector('[data-qty]');
      this.errorEl = this.querySelector('[data-form-error]');

      this.state = {
        variant: this.data.variants.filter(function (v) {
          return v.available;
        })[0] ||
          this.data.variants[0] || { id: null, price: 0, available: false },
        mode: 'one',
        qty: 1,
      };

      var preselected = this.querySelector('.size.active');
      if (preselected && preselected.dataset.variantId) {
        var match = this.findVariant(preselected.dataset.variantId);
        if (match) this.state.variant = match;
      }

      this.bindSizes();
      this.bindModes();
      this.bindQuantity();
      this.bindSubmit();
      this.render();
    }

    findVariant(id) {
      return this.data.variants.filter(function (v) {
        return String(v.id) === String(id);
      })[0];
    }

    bindSizes() {
      var self = this;
      this.sizeButtons = Array.prototype.slice.call(this.querySelectorAll('.size'));
      this.sizeButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          if (button.dataset.available === 'false') return;
          var variant = self.findVariant(button.dataset.variantId);
          if (!variant) return;
          self.state.variant = variant;
          self.sizeButtons.forEach(function (b) {
            b.classList.toggle('active', b === button);
            b.setAttribute('aria-checked', b === button ? 'true' : 'false');
          });
          self.render();
        });
      });
    }

    bindModes() {
      var self = this;
      this.modeButtons = Array.prototype.slice.call(this.querySelectorAll('.mode'));
      this.modeButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          self.state.mode = button.dataset.mode;
          self.modeButtons.forEach(function (b) {
            b.classList.toggle('active', b === button);
            b.setAttribute('aria-checked', b === button ? 'true' : 'false');
          });
          self.render();
        });
      });
    }

    bindQuantity() {
      var self = this;
      this.querySelectorAll('[data-qty-change]').forEach(function (button) {
        button.addEventListener('click', function () {
          var delta = parseInt(button.dataset.qtyChange, 10);
          self.state.qty = Math.max(1, self.state.qty + delta);
          self.render();
        });
      });

      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', function () {
          self.state.qty = Math.max(1, parseInt(self.qtyInput.value, 10) || 1);
          self.render();
        });
      }
    }

    /* the selling plan that matches the current variant, if any */
    sellingPlanId() {
      if (this.state.mode !== 'sub') return null;
      var allocations = this.state.variant.selling_plan_allocations || [];
      return allocations.length ? allocations[0].selling_plan_id : null;
    }

    unitPrice() {
      var planId = this.sellingPlanId();
      if (planId) {
        var allocation = (this.state.variant.selling_plan_allocations || []).filter(function (a) {
          return a.selling_plan_id === planId;
        })[0];
        if (allocation) return allocation.price;
      }
      return this.state.variant.price;
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    render() {
      var variant = this.state.variant;
      var unit = this.unitPrice();
      var total = unit * this.state.qty;

      this.setText('[data-price]', this.money(unit));

      var compare = variant.compare_at_price && variant.compare_at_price > unit ? variant.compare_at_price : null;
      var compareEl = this.querySelector('[data-compare-price]');
      if (compareEl) {
        compareEl.textContent = compare ? this.money(compare) : '';
        compareEl.hidden = !compare;
      }

      var valueEl = this.querySelector('[data-unit-value]');
      if (valueEl) {
        if (variant.grams > 0) {
          valueEl.textContent = this.money(Math.round((unit / variant.grams) * 100) / 100) + ' / gram';
          valueEl.hidden = false;
        } else {
          valueEl.hidden = true;
        }
      }

      this.setText('[data-atc-price]', this.money(total));
      this.setText('[data-sticky-price]', this.money(total));
      this.setText('[data-sticky-size]', variant.option_label || variant.title);

      var shipNote = this.querySelector('[data-ship-note]');
      if (shipNote && this.freeShippingThreshold > 0) {
        shipNote.textContent =
          total >= this.freeShippingThreshold
            ? shipNote.dataset.free
            : (shipNote.dataset.away || '').replace('[amount]', this.money(this.freeShippingThreshold - total));
      }

      if (this.qtyInput && this.qtyInput.value !== String(this.state.qty)) {
        this.qtyInput.value = this.state.qty;
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.variantInput && this.variantInput.value !== String(variant.id || '')) {
        this.variantInput.value = variant.id || '';
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var planId = this.sellingPlanId();
      if (this.sellingPlanInput) {
        var previousPlan = this.sellingPlanInput.value;
        this.sellingPlanInput.value = planId || '';
        this.sellingPlanInput.disabled = !planId;
        if (previousPlan !== this.sellingPlanInput.value) {
          this.sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      var acceleratedCheckout = this.querySelector('[data-accelerated-checkout]');
      if (acceleratedCheckout) acceleratedCheckout.hidden = !variant.available;

      var submit = this.querySelector('[data-atc]');
      if (submit) {
        submit.disabled = !variant.available;
        var label = submit.querySelector('[data-atc-label]');
        if (label) label.textContent = variant.available ? submit.dataset.labelAdd : submit.dataset.labelSoldOut;
        var priceSpan = submit.querySelector('[data-atc-price]');
        if (priceSpan) priceSpan.hidden = !variant.available;
      }

      document.querySelectorAll('[data-sticky-atc]').forEach(function (button) {
        button.disabled = !variant.available;
      });

      document.dispatchEvent(
        new CustomEvent('fk26:variant:change', {
          detail: { variant: variant, featuredMediaId: variant.featured_media_id },
        })
      );

      if (window.history && window.history.replaceState && variant.id) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach(function (el) {
        el.textContent = text;
      });
      document.querySelectorAll('[data-fk26-sticky] ' + selector).forEach(function (el) {
        el.textContent = text;
      });
    }

    bindSubmit() {
      var self = this;
      if (!this.form) return;

      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.addToCart();
      });

      document.querySelectorAll('[data-sticky-atc]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.addToCart();
        });
      });
    }

    addToCart() {
      var self = this;
      var submit = this.querySelector('[data-atc]');
      if (submit) submit.disabled = true;
      if (this.errorEl) this.errorEl.hidden = true;

      var body = {
        items: [
          {
            id: this.state.variant.id,
            quantity: this.state.qty,
          },
        ],
      };
      var planId = this.sellingPlanId();
      if (planId) body.items[0].selling_plan = planId;

      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json().then(function (json) {
            return { ok: response.ok, json: json };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            self.showError(result.json.description || result.json.message);
            return;
          }
          return refreshCartCount().then(function () {
            if (self.dataset.goToCart === 'true') window.location.href = root + 'cart';
          });
        })
        .catch(function () {
          self.showError(self.dataset.genericError);
        })
        .finally(function () {
          if (submit) submit.disabled = !self.state.variant.available;
        });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.genericError || '';
      this.errorEl.hidden = false;
    }
  }

  /* ------------------------------------------------- scroll reveal */
  class FK26Reveal extends HTMLElement {
    connectedCallback() {
      var track = this.querySelector('.timeline');
      if (!track) return;
      var steps = Array.prototype.slice.call(track.querySelectorAll('.tstep'));

      function revealAll() {
        track.classList.add('in');
        steps.forEach(function (step) {
          step.classList.add('in');
        });
      }

      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        revealAll();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            track.classList.add('in');
            steps.forEach(function (step, i) {
              setTimeout(function () {
                step.classList.add('in');
              }, 220 * i);
            });
            observer.disconnect();
          });
        },
        { threshold: 0.35 }
      );

      observer.observe(track);
    }
  }

  /* ---------------------------------------------- bundle carousel */
  class FK26BundleCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.fk26-bundle-track');
      this.controls = this.querySelector('.fk26-bundle-controls');
      if (!this.track || !this.controls) return;
      this.events = new AbortController();
      var signal = this.events.signal;
      var buttons = this.controls.querySelectorAll('button');
      var update = () => {
        var track = this.track;
        var max = track.scrollWidth - track.clientWidth;
        var position = Math.abs(track.scrollLeft);
        this.controls.hidden = max <= 1;
        buttons[0].disabled = position <= 1;
        buttons[1].disabled = position >= max - 1;
      };
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          var card = this.track.querySelector('.xcard');
          if (!card) return;
          var style = getComputedStyle(this.track);
          var distance = card.getBoundingClientRect().width + parseFloat(style.columnGap || 0);
          var direction = Number(button.dataset.direction) * (style.direction === 'rtl' ? -1 : 1);
          this.track.scrollBy({
            left: distance * direction,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        }, { signal: signal });
      });
      this.track.addEventListener('scroll', update, { passive: true, signal: signal });
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(this.track);
      update();
    }

    disconnectedCallback() {
      if (this.events) this.events.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }

  /* --------------------------------------------------------- define */
  if (!customElements.get('fk26-header')) customElements.define('fk26-header', FK26Header);
  if (!customElements.get('fk26-gallery')) customElements.define('fk26-gallery', FK26Gallery);
  if (!customElements.get('fk26-product')) customElements.define('fk26-product', FK26Product);
  if (!customElements.get('fk26-reveal')) customElements.define('fk26-reveal', FK26Reveal);

  if (!customElements.get('fk26-bundle-carousel')) customElements.define('fk26-bundle-carousel', FK26BundleCarousel);

  document.addEventListener('DOMContentLoaded', refreshCartCount);
})();
